import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuctions } from "../../contexts/AuctionsContext";
import { BidAuctionCard } from "../../components/auction/BidAuctionCard";
import { EmptyState } from "../../components/auction/FilterSection";
import type { PublicAuction } from "../../lib/types";
import { useFavoriteAuctions } from "../../lib/use-favorite-auctions";

export function MineBudPage() {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const { applyAuctionEvent } = useAuctions();
  const [bids, setBids] = useState<PublicAuction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { favorites, toggleFavorite } = useFavoriteAuctions();

  function fetchBids() {
    if (!organizationSlug) return;
    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    setLoading(true);
    fetch(`${api}/api/organizations/${organizationSlug}/buyer-bids`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error("Kunne ikke laste bud");
        return res.json() as Promise<PublicAuction[]>;
      })
      .then(setBids)
      .catch((err) => setError(err instanceof Error ? err.message : "Ukjent feil"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchBids();
  }, [organizationSlug]);

  return (
    <div className="mx-auto max-w-screen-xl px-4 sm:px-6 py-4 sm:py-6">
      <div className="mb-8">
        <h1
          className="text-3xl font-black text-[#1a2e35]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Mine bud
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {loading ? "Laster..." : <>Du har lagt inn bud på <strong>{bids.length}</strong> auksjoner</>}
        </p>
      </div>

      {error ? (
        <EmptyState message="Noe gikk galt" sub={error} />
      ) : loading ? (
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-64 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : bids.length === 0 ? (
        <EmptyState
          message="Ingen aktive bud"
          sub="Du har ikke lagt inn noen bud ennå. Gå til budrunder for å finne din neste bil."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {bids.map((item) => (
            <BidAuctionCard
              key={item.id}
              auction={item}
              organizationSlug={organizationSlug ?? ""}
              isFavorite={favorites.includes(item.id)}
              onToggleFavorite={toggleFavorite}
              onBidPlaced={fetchBids}
            />
          ))}
        </div>
      )}
    </div>
  );
}
